import {
  ClaimsProcessor,
  HeadwayVerificationStatus,
  LineOfBusiness,
  ManualVerificationStatus,
  State,
} from "@prisma/client";

import {
  ALWAYS_OON_LINES_OF_BUSINESS,
  type EligibilityCheckStatus,
} from "@marble/core/modules/insurance/constants";
import type {
  InsuranceInfoCompletenessInput,
  InsuranceInfoForNetworkCheck,
  InsuranceVerificationStatuses,
  PayerNetworkStatusForCheck,
} from "@marble/core/modules/insurance/types";

/**
 * Mirror of the {@link NirvanaVerificationStatus} string values used by the
 * eligibility helpers. The full enum lives with the Nirvana client in
 * `apps/web/src/lib/clients/nirvana/constants.ts`; we duplicate the few values
 * actually consumed here so this package stays free of web-app imports.
 */
const NIRVANA_ELIGIBLE = "Eligible";
const NIRVANA_OON = "Out of Network";

export const insuranceInfoIsComplete = (
  insuranceInfo: InsuranceInfoCompletenessInput,
) => {
  return !!(
    insuranceInfo.insurancePayerId &&
    insuranceInfo.memberId &&
    insuranceInfo.memberDOBString &&
    // We don't need student address for headway
    (insuranceInfo.claimsProcessor === ClaimsProcessor.HEADWAY ||
      insuranceInfo.addressId)
  );
};

export const getInnPayerNetworkStatus = ({
  state,
  lineOfBusiness,
  payerNetworkStatuses,
}: {
  state: State;
  lineOfBusiness: LineOfBusiness | null | undefined;
  payerNetworkStatuses: PayerNetworkStatusForCheck[];
}) => {
  if (lineOfBusiness && ALWAYS_OON_LINES_OF_BUSINESS.has(lineOfBusiness)) {
    return undefined;
  }

  return payerNetworkStatuses.find(
    (status) =>
      status.state === state &&
      status.lineOfBusiness === lineOfBusiness &&
      status.isINN,
  );
};

export const getInnPayerNetworkStatusFromInsuranceInfo = (
  insuranceInfo: InsuranceInfoForNetworkCheck | undefined | null,
) => {
  if (!insuranceInfo) return null;
  if (!insuranceInfo.lineOfBusiness) return null;
  return (
    getInnPayerNetworkStatus({
      state: insuranceInfo.state,
      lineOfBusiness: insuranceInfo.lineOfBusiness,
      payerNetworkStatuses:
        insuranceInfo.InsurancePayer?.InsurancePayerNetworkStatus || [],
    }) ?? null
  );
};

export const getIsPayerLobINN = ({
  state,
  lineOfBusiness,
  payerNetworkStatuses,
  considerNotAcceptingNewClientsOON = false,
}: {
  state: State;
  lineOfBusiness: LineOfBusiness | null | undefined;
  payerNetworkStatuses: PayerNetworkStatusForCheck[];
  considerNotAcceptingNewClientsOON?: boolean;
}) => {
  if (!lineOfBusiness) return false;
  if (ALWAYS_OON_LINES_OF_BUSINESS.has(lineOfBusiness)) return false;

  const innIpns = getInnPayerNetworkStatus({
    state,
    lineOfBusiness,
    payerNetworkStatuses,
  });

  if (!innIpns) return false;

  if (considerNotAcceptingNewClientsOON) {
    return innIpns.acceptingNewClients;
  }

  return true;
};

/**
 * Check if insurance is explicitly OON (i.e. incomplete insurance returns false).
 *
 * Note: this considers not accepting new clients as OON.
 */
export const getIsInsuranceInfoOON = (
  insuranceInfo: InsuranceInfoForNetworkCheck | undefined | null,
) => {
  if (!insuranceInfo) return false;

  // Patient explicitly reported no insurance — treat as OON so the auto-
  // refer-out path fires. No payer/LOB is required (or expected) here.
  if (insuranceInfo.doesNotHaveInsurance) return true;

  if (!insuranceInfo.lineOfBusiness || !insuranceInfo.insurancePayerId) {
    return false;
  }

  return !getIsPayerLobINN({
    state: insuranceInfo.state,
    lineOfBusiness: insuranceInfo.lineOfBusiness,
    payerNetworkStatuses:
      insuranceInfo.InsurancePayer?.InsurancePayerNetworkStatus || [],
    considerNotAcceptingNewClientsOON: true,
  });
};

/**
 * Check if insurance is explicitly INN (i.e. incomplete insurance returns false).
 */
export const getIsInsuranceInfoINN = (
  insuranceInfo: InsuranceInfoForNetworkCheck | undefined | null,
  opts: {
    considerNotAcceptingNewClientsOON?: boolean;
  } = {},
) => {
  if (!insuranceInfo) return false;
  return getIsPayerLobINN({
    state: insuranceInfo.state,
    lineOfBusiness: insuranceInfo.lineOfBusiness,
    payerNetworkStatuses:
      insuranceInfo.InsurancePayer?.InsurancePayerNetworkStatus || [],
    considerNotAcceptingNewClientsOON: opts.considerNotAcceptingNewClientsOON,
  });
};

export const getEligibilityCheckStatus = (
  insuranceVerification: InsuranceVerificationStatuses,
  usePendingManualVerificationIfNotSet = false,
): EligibilityCheckStatus => {
  // If manual status set and not NOT_SUPPORTED, use that
  if (
    insuranceVerification.manualVerificationStatus &&
    insuranceVerification.manualVerificationStatus !==
      ManualVerificationStatus.NOT_SUPPORTED
  ) {
    return insuranceVerification.manualVerificationStatus ===
      ManualVerificationStatus.ELIGIBLE
      ? "Eligible"
      : "Ineligible";
  }

  if (insuranceVerification.nirvanaVerificationStatus === NIRVANA_ELIGIBLE) {
    return "Eligible";
  }

  if (insuranceVerification.nirvanaVerificationStatus === NIRVANA_OON) {
    return "Out of Network";
  }

  if (
    !insuranceVerification.manualVerificationStatus &&
    usePendingManualVerificationIfNotSet
  ) {
    return "Pending Manual Verification";
  }

  return "Ineligible";
};

export const getInsuranceVerificationIsPending = (
  insuranceVerification: InsuranceVerificationStatuses,
) => {
  // If we need to setup insurance with Headway, these statuses are considered
  // pending or needs action for Marble or the client, so we don't mistake the
  // insurance as ineligible and action on this client differently.
  if (
    insuranceVerification.headwayVerificationStatus &&
    (insuranceVerification.headwayVerificationStatus ===
      HeadwayVerificationStatus.NEEDS_SUBMISSION_TO_HEADWAY ||
      insuranceVerification.headwayVerificationStatus ===
        HeadwayVerificationStatus.AWAITING_CLIENT_HEADWAY_SETUP ||
      insuranceVerification.headwayVerificationStatus ===
        HeadwayVerificationStatus.HEADWAY_MANUAL_VERIFICATION)
  ) {
    return true;
  }

  return false;
};

export const getInsuranceVerificationIsEligible = (
  insuranceVerification?: InsuranceVerificationStatuses,
) => {
  // A manual denial supersedes any older positive vendor results.
  if (
    insuranceVerification?.manualVerificationStatus ===
    ManualVerificationStatus.INELIGIBLE
  ) {
    return false;
  }

  return (
    insuranceVerification?.manualVerificationStatus ===
      ManualVerificationStatus.ELIGIBLE ||
    insuranceVerification?.nirvanaVerificationStatus === NIRVANA_ELIGIBLE ||
    insuranceVerification?.headwayVerificationStatus ===
      HeadwayVerificationStatus.COMPLETE_ELIGBLE
  );
};
